import React, { useEffect, useState } from 'react';
import { X, RotateCcw, Trash2 } from 'lucide-react';
import { supabase } from '@/core/infrastructure/supabase/supabaseClient';

export type PapeleraBottomSheetProps = Readonly<{
  visible: boolean;
  onClose: () => void;
  onRestore: () => void;
}>;

export function PapeleraBottomSheet({ visible, onClose, onRestore }: PapeleraBottomSheetProps) {
  const [deletedEvents, setDeletedEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchDeletedEvents = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('agenda_eventos')
        .select('*')
        .eq('profesional_uuid', user.id)
        .not('deleted_at', 'is', null)
        .order('deleted_at', { ascending: false });

      if (error) throw error;
      setDeletedEvents(data || []);
    } catch (e) {
      console.error('Error fetching deleted events:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      fetchDeletedEvents();
    }
  }, [visible]);

  const handleRestore = async (eventId: string) => {
    try {
      const { error } = await supabase
        .from('agenda_eventos')
        .update({ deleted_at: null })
        .eq('evento_id', eventId);

      if (error) throw error;
      
      setDeletedEvents(prev => prev.filter(e => e.evento_id !== eventId));
      onRestore();
    } catch (e) {
      console.error('Error restoring event:', e);
    }
  };

  if (!visible) return null;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(15, 23, 42, 0.4)',
      backdropFilter: 'blur(4px)',
      zIndex: 100,
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'center',
    }}>
      <div style={{
        backgroundColor: '#FFFFFF',
        width: '100%',
        maxWidth: '500px',
        borderRadius: '24px 24px 0 0',
        padding: '24px 20px',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 -10px 40px rgba(0,0,0,0.1)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Trash2 size={22} color="#475569" />
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#0F172A' }}>Papelera de Reciclaje</h3>
          </div>
          <button 
            onClick={onClose}
            style={{
              background: '#F1F5F9', border: 'none', borderRadius: '50%',
              width: '32px', height: '32px', display: 'flex', alignItems: 'center',
              justifyContent: 'center', cursor: 'pointer', color: '#475569'
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {(() => {
            if (loading) {
              return <div style={{ textAlign: 'center', padding: '20px', color: '#64748B' }}>Cargando papelera...</div>;
            }
            if (deletedEvents.length === 0) {
              return (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94A3B8' }}>
                  <Trash2 size={40} style={{ marginBottom: '12px', opacity: 0.5 }} />
                  <div style={{ fontSize: '15px', fontWeight: 600 }}>Papelera vacía</div>
                  <div style={{ fontSize: '13px', marginTop: '4px' }}>Las citas eliminadas aparecerán aquí.</div>
                </div>
              );
            }
            return deletedEvents.map((event) => (
              <div 
                key={event.evento_id}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '16px', borderRadius: '16px', border: '1px solid #E2E8F0',
                  backgroundColor: '#F8FAFC'
                }}
              >
                <div>
                  <div style={{ fontWeight: 700, color: '#1E293B', fontSize: '14px' }}>
                    {event.paciente_data?.nombre || event.nota || 'Bloqueo de Horario'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748B', marginTop: '2px' }}>
                    {event.fecha} • {event.inicio.slice(0, 5)} - {event.fin.slice(0, 5)}
                  </div>
                </div>
                <button
                  onClick={() => handleRestore(event.evento_id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '8px 12px', backgroundColor: '#E0F2FE', border: 'none',
                    borderRadius: '8px', color: '#0369A1', fontWeight: 600, fontSize: '13px',
                    cursor: 'pointer'
                  }}
                >
                  <RotateCcw size={14} />
                  Restaurar
                </button>
              </div>
            ));
          })()}
        </div>
      </div>
    </div>
  );
}
